import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, FileText, Filter, CheckCircle2, Building, ExternalLink, ShoppingBag } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { products } from '../data/products';
import { ProductPlaceholder } from '../components/ProductPlaceholder';
import { SquareCard } from '../components/SquareCard';
import { getShopCollectionUrl, getShopProductUrl } from '../data/shopUrls';
import { PartnerCTA } from '../components/PartnerCTA';
export function ProductsHubPage() {
  const [filter, setFilter] = useState('all');
  const location = useLocation();
  // Handle hash navigation for categories and query param for sector filter
  useEffect(() => {
    // Check for sector query param (from sector pages)
    const params = new URLSearchParams(location.search);
    const sectorParam = params.get('sector');
    if (sectorParam) {
      setFilter(sectorParam);
    }
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }
  }, [location]);
  const categories = [{
    slug: 'bags-backpacks',
    name: 'Bags and Backpacks',
    desc: 'Load carriage systems'
  }, {
    slug: 'clothing',
    name: 'Clothing',
    desc: 'Uniforms & protective wear'
  }, {
    slug: 'fire-retardants',
    name: 'Fire Retardants',
    desc: 'Textiles & treatments'
  }, {
    slug: 'public-order',
    name: 'Public Order',
    desc: 'Shields & restraints'
  }, {
    slug: 'tactical-vests',
    name: 'Tactical Vests',
    desc: 'Body armour carriers'
  }, {
    slug: 'training-equipment',
    name: 'Training Equipment',
    desc: 'Suits & targets'
  }];
  const sectors = ['Defence', 'Police', 'Prisons', 'Medical'];
  return <>
      <PageHero label="Product Catalogue" title="Specialist Equipment Range" subtitle="Products are showcased here for reference. Full catalogue and ordering will be available via our inventory. All products are manufactured to strict technical specifications and are available via public sector frameworks." data-id="element-1334" />

      <Breadcrumbs items={[{
      label: 'Products'
    }]} data-id="element-1335" />

      {/* Category Grid - Top Navigation - Square Card Style */}
      <section className="bg-white py-12 border-b border-steel-100" data-id="element-1336">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1337">
          <h2 className="text-xl font-bold text-navy-900 mb-8" data-id="element-1338">
            Browse by Category
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1339">
            {categories.map(cat => <SquareCard key={cat.slug} to={getShopCollectionUrl(cat.slug)} isExternal={true} title={cat.name} description={cat.desc} actionText="Browse Inventory" variant="category" data-id="element-1340" />)}
          </div>
        </div>
      </section>

      {/* Organisation Procurement Panel */}
      <section className="bg-white border-b border-steel-100" data-id="element-1341">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8" data-id="element-1342">
          <div className="bg-slate-50 border border-steel-200 border-l-4 border-l-cg-orange-600 p-6 flex flex-col md:flex-row items-start md:items-center gap-4" data-id="element-1343">
            <Building className="w-6 h-6 text-cg-orange-600 flex-shrink-0 mt-0.5" data-id="element-1344" />
            <div className="flex-1" data-id="element-1345">
              <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-1" data-id="element-1346">
                Organisation Procurement
              </h3>
              <p className="text-sm text-steel-600" data-id="element-1347">
                All orders are processed as approval-based requests. No payment
                is taken online. We support purchase orders and invoice-based
                payment for approved public sector organisations.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0" data-id="element-1348">
              <Link to="/frameworks" className="px-4 py-2 bg-navy-900 text-white font-bold uppercase tracking-wider text-xs rounded-sm hover:bg-cg-orange-600 transition-colors whitespace-nowrap text-center" data-id="element-1349">
                View Frameworks
              </Link>
              <Link to="/contact" className="px-4 py-2 border border-navy-900 text-navy-900 font-bold uppercase tracking-wider text-xs rounded-sm hover:bg-navy-900 hover:text-white transition-colors whitespace-nowrap text-center" data-id="element-1350">
                Request Quotation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Filter Bar */}
      <div className="sticky top-[72px] z-40 bg-white border-b border-steel-200 shadow-sm" data-id="element-1351">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4" data-id="element-1352">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-1353">
            <div className="flex items-center gap-2 text-sm font-bold text-navy-900 uppercase tracking-wider" data-id="element-1354">
              <Filter className="w-4 h-4 text-cg-orange-600" data-id="element-1355" /> Filter by
              Application:
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-1356">
              <button onClick={() => setFilter('all')} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${filter === 'all' ? 'bg-navy-900 text-white' : 'bg-steel-100 text-steel-600 hover:bg-steel-200'}`} data-id="element-1357">
                All Sectors
              </button>
              {sectors.map(sector => <button key={sector} onClick={() => setFilter(sector.toLowerCase())} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${filter === sector.toLowerCase() ? 'bg-cg-orange-600 text-white' : 'bg-steel-100 text-steel-600 hover:bg-steel-200'}`} data-id="element-1358">
                  {sector}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-50 py-12" data-id="element-1359">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-20" data-id="element-1360">
          {categories.map(category => {
          const categoryProducts = products.filter(p => p.category === category.slug && (filter === 'all' || p.sectors.includes(filter)));
          if (categoryProducts.length === 0) return null;
          return <section key={category.slug} id={category.slug} className="scroll-mt-40" data-id="element-1361">
                {/* Dossier Style Header */}
                <div className="flex items-end justify-between mb-8 border-t border-steel-200 pt-8" data-id="element-1362">
                  <h2 className="text-3xl font-bold text-navy-900" data-id="element-1363">
                    {category.name}
                  </h2>
                  <span className="text-xs font-bold text-white bg-navy-900 px-2 py-1 rounded-sm uppercase tracking-wider" data-id="element-1364">
                    {categoryProducts.length} Items
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1365">
                  {categoryProducts.map(product => <a key={product.slug} href={getShopProductUrl(product.slug)} target="_blank" rel="noopener noreferrer" className="group bg-white border border-steel-200 hover:border-cg-orange-600 transition-all duration-300 flex flex-col shadow-sm hover:shadow-md" data-id="element-1366">
                      <div className="aspect-[4/3] overflow-hidden bg-slate-100 relative" data-id="element-1367">
                        <ProductPlaceholder aspectRatio="4/3" className="w-full h-full" data-id="element-1368" />
                        <div className="absolute top-4 left-4 flex flex-col gap-1" data-id="element-1369">
                          {product.sectors.slice(0, 2).map(s => <span key={s} className="bg-navy-900/90 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm w-fit" data-id="element-1370">
                              {s}
                            </span>)}
                        </div>
                        <div className="absolute top-4 right-4" data-id="element-1371">
                          <span className="bg-cg-orange-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm shadow-sm" data-id="element-1372">
                            Inventory
                          </span>
                        </div>
                      </div>

                      <div className="p-6 flex-1 flex flex-col" data-id="element-1373">
                        <h3 className="text-xl font-bold text-navy-900 mb-2 group-hover:text-cg-orange-600 transition-colors" data-id="element-1374">
                          {product.name}
                        </h3>
                        <p className="text-sm text-steel-600 mb-6 line-clamp-2 flex-1" data-id="element-1375">
                          {product.shortDescription}
                        </p>

                        <div className="space-y-2 mb-6" data-id="element-1376">
                          <div className="flex items-center gap-2 text-xs text-steel-500" data-id="element-1377">
                            <CheckCircle2 className="w-3 h-3 text-cg-orange-600" data-id="element-1378" />
                            <span data-id="element-1379">Specification-led options</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-steel-500" data-id="element-1380">
                            <CheckCircle2 className="w-3 h-3 text-cg-orange-600" data-id="element-1381" />
                            <span data-id="element-1382">Framework-aligned</span>
                          </div>
                        </div>

                        <div className="pt-4 border-t border-steel-100 flex justify-between items-center" data-id="element-1383">
                          <span className="text-xs font-bold text-navy-900 uppercase tracking-wider flex items-center gap-1 group-hover:text-cg-orange-600 transition-colors" data-id="element-1384">
                            View in Inventory{' '}
                            <ExternalLink className="w-3 h-3 group-hover:translate-x-1 transition-transform" data-id="element-1385" />
                          </span>
                          <span className="text-xs font-bold text-steel-400 uppercase tracking-wider flex items-center gap-1" data-id="element-1386">
                            <FileText className="w-3 h-3" data-id="element-1387" /> Specs
                          </span>
                        </div>
                      </div>
                    </a>)}
                </div>
              </section>;
        })}
        </div>
      </div>

      <PartnerCTA data-id="element-1388" />
    </>;
}